/**
 * /app/$tenantSlug/accounting/post-to-books — Accounting → Post to books.
 *
 * HR money events that happened before automatic posting existed (or
 * while the Accounting module was off) never reach the books by
 * themselves:
 *   - payroll runs locked without a salary journal,
 *   - expense batches marked paid whose payment was never posted,
 *   - F&F settlements computed without a settlement journal,
 *   - loans disbursed without a disbursement journal (2026-10 re-test
 *     books #14 — 1210 Employee Loan Receivable left in credit; each loan
 *     posts on its own date through /loans/{id}/post-disbursement).
 * This page lists each with the exact journal it would post (period-lock
 * and missing-account problems flagged) and posts only what the user
 * ticks. Each item posts in its own transaction server-side, so one
 * locked period or missing account stops that item only.
 *
 * Endpoints (lib/api/books-posting.ts): GET/POST
 * /api/v1/accounting/backlog/{payroll-runs,expense-batches,ff-settlements};
 * GET /api/v1/accounting/backlog/loan-disbursements (lib/api/books-backlog-loans.ts).
 * Gates: AccountingModuleShell = feature "accounting" + role company_admin
 * / hr_manager / accountant (the server's accountingRole). Linked from the
 * Accounting module strip and the payroll run list's "not in your books"
 * note.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Info } from "lucide-react";
import { useAuth, useFeatures } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import { AccountingModuleShell } from "@/components/shared/accounting-module-shell";
import { BacklogPayrollRuns } from "@/components/accounting/books-backlog-payroll";
import { BacklogExpenseBatches } from "@/components/accounting/books-backlog-expenses";
import { BacklogFFSettlements } from "@/components/accounting/books-backlog-ff";
import { BacklogLoanDisbursements } from "@/components/accounting/books-backlog-loans";
import { useBacklogLoanDisbursements } from "@/hooks/use-books-backlog-loans";
import {
  useBacklogExpenseBatches,
  useBacklogFFSettlements,
  useBacklogPayrollRuns,
} from "@/hooks/use-books-posting";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/accounting/post-to-books",
)({
  component: PostToBooksRoute,
});

type Tab = "payroll" | "expenses" | "ff" | "loans";

/** "(3)" after a tab label once its list has loaded and is non-empty. */
function TabCount({ count }: Readonly<{ count: number | undefined }>) {
  if (count == null || count === 0) return null;
  return (
    <span className="ml-1.5 rounded-full bg-amber-100 px-1.5 text-xs font-medium tabular-nums text-amber-900">
      {count}
    </span>
  );
}

function PostToBooksRoute() {
  const [tab, setTab] = useState<Tab>("payroll");
  const { user } = useAuth();
  const { has } = useFeatures();
  // The shell's gates render the sections; these count queries run at
  // the route level, so they carry the same gate (no stray 403s).
  const allowed = has("accounting") && isAccountingAdmin(user?.role);
  // Same queries (and cache entries) the sections read — only for the
  // tab counts. The expense count uses the recorded-wallet preview.
  const runsQ = useBacklogPayrollRuns(allowed);
  const batchesQ = useBacklogExpenseBatches(undefined, allowed);
  const ffQ = useBacklogFFSettlements(allowed);
  const loansQ = useBacklogLoanDisbursements(allowed);

  return (
    <AccountingModuleShell
      title="Post to Books"
      description="Payroll, expense payments, F&F and loan disbursements that never reached your books."
    >
      <Alert className="mb-4">
        <Info aria-hidden className="h-4 w-4" />
        <AlertDescription>
          Runs locked, expense batches paid, F&F settlements computed and
          loans disbursed before automatic posting existed (or while
          Accounting was off) are not in your books. Nothing is posted automatically: review each
          journal below and post the ones that belong. Each item posts on its
          own; a locked period or a missing account only stops that item.
        </AlertDescription>
      </Alert>

      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
        {/* Phones: an even 2 × 2 grid with short labels; sm+ the row. */}
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:inline-flex sm:w-auto sm:flex-wrap">
          <TabsTrigger value="payroll" className="[@media(pointer:coarse)]:min-h-11" data-testid="post-to-books-tab-payroll">
            <span className="sm:hidden">Payroll</span>
            <span className="hidden sm:inline">Payroll Runs</span>
            <TabCount count={runsQ.data?.items.length} />
          </TabsTrigger>
          <TabsTrigger value="expenses" className="[@media(pointer:coarse)]:min-h-11" data-testid="post-to-books-tab-expenses">
            <span className="sm:hidden">Expenses</span>
            <span className="hidden sm:inline">Expense Batches</span>
            <TabCount count={batchesQ.data?.items.length} />
          </TabsTrigger>
          <TabsTrigger value="ff" className="[@media(pointer:coarse)]:min-h-11" data-testid="post-to-books-tab-ff">
            <span className="sm:hidden">F&amp;F</span>
            <span className="hidden sm:inline">F&amp;F Settlements</span>
            <TabCount count={ffQ.data?.items.length} />
          </TabsTrigger>
          <TabsTrigger value="loans" className="[@media(pointer:coarse)]:min-h-11" data-testid="post-to-books-tab-loans">
            <span className="sm:hidden">Loans</span>
            <span className="hidden sm:inline">Loan Disbursements</span>
            <TabCount count={loansQ.data?.items.length} />
          </TabsTrigger>
        </TabsList>
        <TabsContent value="payroll" className="mt-4">
          <BacklogPayrollRuns />
        </TabsContent>
        <TabsContent value="expenses" className="mt-4">
          <BacklogExpenseBatches />
        </TabsContent>
        <TabsContent value="ff" className="mt-4">
          <BacklogFFSettlements />
        </TabsContent>
        <TabsContent value="loans" className="mt-4">
          <BacklogLoanDisbursements />
        </TabsContent>
      </Tabs>
    </AccountingModuleShell>
  );
}
